import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { APP_NAME, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CONSOLE_URL, STORE_URL } from "@/lib/sites";
import PhoneLogin from "@/components/PhoneLogin";
import { confirmCode, friendlyError, phoneLoginConfig, sendCode } from "@/lib/firebasePhone";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Module 10: one customer sign-in, sign-up, confirm and forgot-password look
// on the Shop, Care and Community websites. The same file is copied into
// frontend/, care/ and community/ (keep the three copies the same).

/** A page on this website to go back to (never another site). */
export const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") ? n : null);
const nextQuery = (n) => (n ? `?next=${encodeURIComponent(n)}` : "");
const confirmPath = (n) => `/confirm?new=1${n ? `&next=${encodeURIComponent(n)}` : ""}`;
const placeName = (n) => {
  if (!n) return null;
  if (n.startsWith("/cart")) return "your cart";
  if (n.startsWith("/checkout")) return "checkout";
  if (n.startsWith("/services/") || n.startsWith("/salons/")) return "the provider you were looking at";
  return "the page you were on";
};
// Policies live on the shop website.
const shopHref = (path) => (APP_NAME === "store" ? path : `${STORE_URL}${path}`);
const errText = (err) => formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Something went wrong";

const box = "text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3";
const dark = "w-full h-12 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold";

function PasswordInput({ id, value, onChange, testid, autoComplete = "current-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-center mt-1 rounded-md border border-input bg-white pr-1.5 focus-within:ring-2 focus-within:ring-ring">
      <input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} required autoComplete={autoComplete}
        className="flex-1 h-11 bg-transparent px-3 text-sm outline-none" data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-pressed={show} data-testid={`${testid}-show`}
        className="h-8 px-3 rounded-full bg-stone-100 text-xs font-semibold text-stoneWarm">{show ? "Hide" : "Show"}</button>
    </div>
  );
}

function Shell({ children }) {
  return <div className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-12"><Card className="p-6 sm:p-8 rounded-3xl border-stone-200">{children}</Card></div>;
}

// ------------------------------------------------------------------ Sign in
export function SignInPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = safeNext(sp.get("next"));
  const { user, loginWith, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Mobile first when SMS sign-in is set up on the server, else email only.
  const [method, setMethod] = useState(sp.get("method") === "email" ? "email" : null);
  const [phoneOn, setPhoneOn] = useState(false);
  const [expired] = useState(() => {
    try { const v = sessionStorage.getItem("pm_session_expired"); sessionStorage.removeItem("pm_session_expired"); return !!v; }
    catch (e) { return false; }
  });
  // Signing in here decides where to go next; the guard is for arriving signed in.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    phoneLoginConfig().then((c) => { setPhoneOn(!!c.enabled); setMethod((m) => m || (c.enabled ? "phone" : "email")); });
  }, []);

  if (user && !busy && !leaving) return <Navigate to={next || "/"} replace />;

  const done = async (data) => {
    setLeaving(true);
    loginWith(data.token, data.user);
    await refresh();
    const first = (data.user.name || "").split(" ")[0];
    toast.success(data.new_account ? `Welcome to PawBazaar, ${first}!` : `Welcome back, ${first}!`);
    // A new account (mobile code sign-up) confirms its email next.
    navigate(data.new_account ? confirmPath(next) : next || "/", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, app: APP_NAME });
      await done(data);
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Sign in</h1>
      <p className="text-sm text-stone-600 mt-1">One PawBazaar account for Shop, Care and Community.</p>
      {expired && <div data-testid="login-expired-banner" className="mt-4 rounded-xl bg-marigold/10 border border-marigold/30 px-3 py-2.5 text-xs text-stoneWarm">Your session ended. Please sign in again.</div>}
      {phoneOn && (
        <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-stone-100 mt-5" data-testid="login-method" role="tablist">
          {[["phone", "Mobile number"], ["email", "Email"]].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={method === k} onClick={() => { setMethod(k); setError(""); }} data-testid={`login-method-${k}`}
              className={`h-9 rounded-full text-sm font-medium ${method === k ? "bg-white shadow-sm text-stoneWarm" : "text-stone-500"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="mt-5">
        {method === null ? <Loader2 className="h-4 w-4 animate-spin" /> : method === "phone" ? <PhoneLogin onSignedIn={done} /> : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 mt-1" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to={`/forgot-password${nextQuery(next)}`} className="text-xs text-teal hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password-input" />
            </div>
            {error && <div data-testid="login-error" className={box}>{error}</div>}
            <Button type="submit" disabled={busy} className={dark} data-testid="login-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
          </form>
        )}
      </div>
      <div className="mt-6 text-sm text-stone-600 text-center">
        New to PawBazaar? <Link to={`/register${nextQuery(next)}`} className="text-terracotta font-semibold hover:underline" data-testid="login-register-link">Create your account</Link>
      </div>
      <div className="mt-4 pt-4 border-t border-stone-200 text-xs text-stone-500 text-center" data-testid="login-business-hint">
        Own a pet shop, care service or NGO? <a href={`${CONSOLE_URL}/login`} className="text-teal font-semibold hover:underline">Business sign-in</a>
      </div>
    </Shell>
  );
}

// ------------------------------------------------------------------ Sign up
export function SignUpPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = safeNext(sp.get("next"));
  const { user, loginWith, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value }));
  const back = placeName(next);
  const [leaving, setLeaving] = useState(false);

  if (user && !busy && !leaving) return <Navigate to={next || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) return setError("Enter your 10-digit mobile number");
    if (form.password.length < 8) return setError("The password needs at least 8 characters");
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, name: form.name.trim(), email: form.email.trim() });
      setLeaving(true);
      loginWith(data.token, data.user);
      await refresh();
      navigate(confirmPath(next), { replace: true });
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Create your account</h1>
      <p className="text-sm text-stone-600 mt-1">Shop for your pet, book care and join the community with one account.</p>
      {back && <div className="mt-3 rounded-xl bg-teal/5 border border-teal/20 px-3 py-2 text-xs text-stone-700" data-testid="register-back-note">After this you go back to {back}.</div>}
      <form onSubmit={submit} className="space-y-4 mt-5">
        <div>
          <Label htmlFor="r-name">Full name</Label>
          <Input id="r-name" autoComplete="name" required value={form.name} onChange={set("name")} className="h-11 mt-1" data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="r-phone">Mobile number</Label>
          <div className="flex mt-1">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-stone-50 text-sm text-stone-600">+91</span>
            <Input id="r-phone" inputMode="numeric" autoComplete="tel-national" required value={form.phone} onChange={set("phone")} placeholder="10-digit number" className="h-11 rounded-l-none" data-testid="register-phone-input" />
          </div>
          <p className="text-xs text-stone-500 mt-1">For delivery updates and signing in with a code.</p>
        </div>
        <div>
          <Label htmlFor="r-email">Email</Label>
          <Input id="r-email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} className="h-11 mt-1" data-testid="register-email-input" />
          <p className="text-xs text-stone-500 mt-1">For bills and receipts.</p>
        </div>
        <div>
          <Label htmlFor="r-password">Password</Label>
          <PasswordInput id="r-password" value={form.password} onChange={set("password")} testid="register-password-input" autoComplete="new-password" />
          <p className="text-xs text-stone-500 mt-1">At least 8 characters.</p>
        </div>
        {error && <div data-testid="register-error" className={box}>{error}</div>}
        <Button type="submit" disabled={busy} className={dark} data-testid="register-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
        <p className="text-xs text-stone-500 text-center">By creating an account you agree to the <a href={shopHref("/policies/terms")} className="text-teal hover:underline">terms</a> and <a href={shopHref("/policies/privacy")} className="text-teal hover:underline">privacy policy</a>.</p>
      </form>
      <div className="mt-5 text-sm text-stone-600 text-center">
        Already have an account? <Link to={`/login${nextQuery(next)}`} className="text-terracotta font-semibold hover:underline">Sign in</Link>
      </div>
    </Shell>
  );
}

// ------------------------------------------------------------------ Confirm mobile and email
function useWait() {
  const [wait, setWait] = useState(0);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  return [wait, setWait];
}

function devCode(data) {
  const c = data?.debug_code;
  if (c) toast.info(`Test copy: the code is ${c}`, { duration: 15000 });
}

function EmailRow({ user, onDone, autoSent }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(!!autoSent);
  const [wait, setWait] = useWait();
  const ok = !(user.contact_missing || []).includes("email");

  const send = async () => {
    setBusy(true); setError("");
    try { const { data } = await api.post("/auth/verify/send", { channel: "email" }); devCode(data); setSent(true); setWait(30); if (data.already_verified) await onDone(); }
    catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault();
    if (!/^\d{4,10}$/.test(code)) return setError("Enter the code from the email");
    setBusy(true); setError("");
    try { await api.post("/auth/verify/confirm", { channel: "email", code }); toast.success("Email confirmed"); await onDone(); }
    catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="confirm-email-row">
      <div className="flex items-start justify-between gap-3 text-sm">
        <div><div className="font-semibold">Email</div><div className="text-stone-600 break-all">{user.email}</div></div>
        <span className={`shrink-0 text-xs font-bold ${ok ? "text-teal" : "text-amber-700"}`} data-testid="confirm-email-status">{ok ? "Confirmed" : "To confirm"}</span>
      </div>
      {!ok && (
        <form onSubmit={confirm} className="mt-3 space-y-2">
          {sent ? <Label htmlFor="c-email">Enter the 6-digit code we sent to your email</Label>
            : <p className="text-xs text-stone-600">We send a 6-digit code to this email.</p>}
          {sent && <Input id="c-email" inputMode="numeric" autoComplete="one-time-code" maxLength={10} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-11 font-mono tracking-[0.3em]" data-testid="confirm-email-code" />}
          {error && <div className={box}>{error}</div>}
          <div className="flex items-center gap-3">
            {sent && <Button type="submit" disabled={busy} className="h-10 rounded-full bg-stoneWarm text-white px-5" data-testid="confirm-email-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm email"}</Button>}
            {wait > 0 ? <span className="text-xs text-stone-500">Send again in {wait}s</span>
              : <button type="button" onClick={send} disabled={busy} className="text-sm text-teal font-semibold hover:underline" data-testid="confirm-email-send">{sent ? "Send again" : "Send code"}</button>}
          </div>
          {sent && <p className="text-xs text-stone-500">Didn't get it? Check spam, or send again.</p>}
        </form>
      )}
    </div>
  );
}

function PhoneRow({ user, onDone }) {
  const mine = (user.phone || "").replace(/^\+91/, "").replace(/\D/g, "").slice(-10);
  const [phone, setPhone] = useState(mine);
  const [code, setCode] = useState("");
  const [how, setHow] = useState(null);   // firebase | sms | none
  const [confirmation, setConfirmation] = useState(null);
  const [sent, setSent] = useState(false);
  const [password, setPassword] = useState("");
  const [needPassword, setNeedPassword] = useState(null);   // the id token waiting for "confirm it's you"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wait, setWait] = useWait();
  const needed = (user.contact_missing || []).includes("phone");
  const ok = !needed;

  useEffect(() => {
    phoneLoginConfig().then((c) => {
      if (c.enabled) return setHow("firebase");
      api.get("/config/defaults").then(({ data }) => setHow(data.sms_enabled ? "sms" : "none")).catch(() => setHow("none"));
    });
  }, []);

  const send = async () => {
    if (!/^[6-9]\d{9}$/.test(phone)) return setError("Enter your 10-digit mobile number");
    setBusy(true); setError("");
    try {
      if (how === "firebase") setConfirmation(await sendCode(phone, "confirm-phone-recaptcha"));
      else { const { data } = await api.post("/auth/verify/send", { channel: "phone" }); devCode(data); }
      setSent(true); setWait(30); setCode("");
    } catch (err) { setError(err.response ? errText(err) : friendlyError(err)); }
    finally { setBusy(false); }
  };
  const link = async (idToken) => {
    try { await api.post("/auth/phone/link", { id_token: idToken }); }
    catch (err) {
      // A new sign-in number needs a recent sign-in; this site asks for the password here.
      if (err.response?.data?.detail?.code === "reauth_required" && user.has_password) { setNeedPassword(idToken); return false; }
      throw err;
    }
    return true;
  };
  const confirm = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code");
    setBusy(true); setError("");
    try {
      if (how === "firebase") {
        let token;
        try { token = await confirmCode(confirmation, code); } catch (err) { setError(friendlyError(err)); return; }
        if (!(await link(token))) return;
      } else {
        await api.post("/auth/verify/confirm", { channel: "phone", code });
      }
      toast.success("Mobile number confirmed");
      await onDone();
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };
  const reauth = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/reauth", { password });
      await link(needPassword);
      setNeedPassword(null);
      toast.success("Mobile number confirmed");
      await onDone();
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="confirm-phone-row">
      <div className="flex items-start justify-between gap-3 text-sm">
        <div><div className="font-semibold">Mobile</div><div className="text-stone-600">{user.phone ? `+91 ${mine}` : "Not added yet"}</div></div>
        <span className={`shrink-0 text-xs font-bold ${user.phone_verified ? "text-teal" : needed ? "text-amber-700" : "text-stone-500"}`} data-testid="confirm-phone-status">
          {user.phone_verified ? "Confirmed" : needed ? "To confirm" : "Later"}</span>
      </div>
      {!user.phone_verified && !needed && <p className="mt-2 text-xs text-stone-600">Mobile codes aren't switched on yet. We'll ask for it later.</p>}
      {!ok && how === "none" && <p className="mt-2 text-xs text-stone-600">Mobile codes can't be sent right now. Please try again later.</p>}
      {!ok && how && how !== "none" && (needPassword ? (
        <form onSubmit={reauth} className="mt-3 space-y-2">
          <Label htmlFor="c-pass">For your safety, enter your password to finish</Label>
          <PasswordInput id="c-pass" value={password} onChange={(e) => setPassword(e.target.value)} testid="confirm-phone-password" />
          {error && <div className={box}>{error}</div>}
          <Button type="submit" disabled={busy} className="h-10 rounded-full bg-stoneWarm text-white px-5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}</Button>
        </form>
      ) : (
        <form onSubmit={confirm} className="mt-3 space-y-2">
          {!sent && how === "firebase" && (
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-stone-50 text-sm text-stone-600">+91</span>
              <Input inputMode="numeric" aria-label="Mobile number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} className="h-11 rounded-l-none" data-testid="confirm-phone-number" />
            </div>
          )}
          {sent && <>
            <Label htmlFor="c-phone">Enter the 6-digit code sent by SMS to +91 {phone}</Label>
            <Input id="c-phone" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-11 font-mono tracking-[0.3em]" data-testid="confirm-phone-code" />
          </>}
          {error && <div className={box}>{error}</div>}
          <div className="flex items-center gap-3">
            {sent && <Button type="submit" disabled={busy} className="h-10 rounded-full bg-stoneWarm text-white px-5" data-testid="confirm-phone-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm mobile"}</Button>}
            {wait > 0 ? <span className="text-xs text-stone-500">Send again in {wait}s</span>
              : <button type="button" onClick={send} disabled={busy} className="text-sm text-teal font-semibold hover:underline" data-testid="confirm-phone-send">{sent ? "Send again" : "Send code"}</button>}
          </div>
        </form>
      ))}
      <div id="confirm-phone-recaptcha" />
    </div>
  );
}

/** The mobile and email rows; renders nothing once both are confirmed.
 *  `checkout` shows it as the "One step before your first order" box. */
export function ContactCheck({ checkout = false, autoSent = false }) {
  const { user, refresh } = useAuth();
  const missing = user?.contact_missing || [];
  if (!user || (checkout && missing.length === 0)) return null;
  const rows = (
    <div className="space-y-3">
      <PhoneRow user={user} onDone={refresh} />
      <EmailRow user={user} onDone={refresh} autoSent={autoSent} />
    </div>
  );
  if (!checkout) return rows;
  return (
    <section className="rounded-2xl border border-amber-400 bg-amber-50/60 p-4 space-y-3" data-testid="contact-check">
      <div>
        <div className="font-semibold">One step before your order</div>
        <p className="text-sm text-stone-700 mt-0.5">Confirm your {missing.map((m) => (m === "phone" ? "mobile number" : "email")).join(" and ")} so we can send your bill and updates. Pay opens once both are confirmed.</p>
      </div>
      {rows}
    </section>
  );
}

/** True while this customer still has to confirm something before paying. */
export const mustConfirm = (user) => !!user && (user.contact_missing || []).length > 0;

export function ConfirmPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNext(sp.get("next"));
  const isNew = sp.get("new") === "1";
  const { user, loading } = useAuth();
  if (!user) return loading ? null : <Navigate to={`/login${nextQuery(`/confirm${next ? `?next=${encodeURIComponent(next)}` : ""}`)}`} replace />;
  const allDone = !mustConfirm(user);
  return (
    <Shell>
      {isNew && <div className="text-xs font-bold text-teal mb-1">Account created</div>}
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Confirm your mobile and email</h1>
      <p className="text-sm text-stone-600 mt-1">We use them for orders, delivery updates and signing in. Both must be confirmed before your first order.</p>
      <div className="mt-5"><ContactCheck autoSent={isNew} /></div>
      <Button onClick={() => navigate(next || "/", { replace: true })} className={`${dark} mt-5`} data-testid="confirm-continue">
        {allDone ? "Continue" : "Do it later"}
      </Button>
      {!allDone && <p className="text-xs text-stone-500 text-center mt-2">You can browse and add to your cart now. We'll ask again before you pay.</p>}
    </Shell>
  );
}

// ------------------------------------------------------------------ Forgot password (3 steps)
function StepItem({ n, step, title, sub, children }) {
  const done = step > n, here = step === n;
  return (
    <li className={`rounded-2xl border p-3.5 ${here ? "border-teal bg-white" : "border-stone-200 bg-white"}`} data-testid={`forgot-step-${n}`}>
      <div className="flex items-center gap-3">
        <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-teal text-white" : here ? "bg-stoneWarm text-white" : "border border-stone-400 text-stone-500"}`}>{done ? "✓" : n}</span>
        <div className="text-sm"><b>{title}</b>{sub && <div className="text-stone-600">{sub}</div>}</div>
      </div>
      {here && children && <div className="mt-3">{children}</div>}
    </li>
  );
}

export function ForgotPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNext(sp.get("next"));
  const [step, setStep] = useState(sp.get("token") ? 3 : 1);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(sp.get("token") || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [phoneOn, setPhoneOn] = useState(false);
  useEffect(() => { phoneLoginConfig().then((c) => setPhoneOn(!!c.enabled)); }, []);

  const go = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (step === 1) {
        const { data } = await api.post("/auth/forgot-password", { email: email.trim(), app: APP_NAME });
        if (data.debug_token) toast.info(`Test copy: the code is ${data.debug_token}`, { duration: 20000 });
        setStep(2);
      } else if (step === 2) {
        if (token.trim().length < 10) throw new Error("Paste the code from the email");
        setStep(3);
      } else {
        if (password.length < 8) throw new Error("The password needs at least 8 characters");
        const { data } = await api.post("/auth/reset-password", { token: token.trim(), new_password: password });
        toast.success(data.message || "Password updated. Sign in with your new password.");
        navigate(`/login?method=email${next ? `&next=${encodeURIComponent(next)}` : ""}`, { replace: true });
      }
    } catch (err) {
      setError(err.response ? errText(err) : err.message);
      if (step === 3 && err.response?.status === 400) setStep(2);
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <Link to={`/login${nextQuery(next)}`} className="text-sm font-semibold text-teal hover:underline">Back to sign in</Link>
      <h1 className="font-heading text-3xl font-semibold tracking-tight mt-3">Set a new password</h1>
      <p className="text-sm text-stone-600 mt-1">Three short steps.</p>
      <form onSubmit={go}>
        <ol className="mt-5 space-y-2.5">
          <StepItem step={step} n={1} title="Your email" sub={step > 1 ? email : null}>
            <Input type="email" aria-label="Email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="forgot-email-input" />
          </StepItem>
          <StepItem step={step} n={2} title="Check your email" sub={step === 2 ? "If this email has an account, we sent a link and a code. Open the link, or paste the code here." : null}>
            <Input aria-label="Code from the email" value={token} onChange={(e) => setToken(e.target.value)} className="h-11 font-mono" placeholder="Code from the email" data-testid="forgot-code-input" />
          </StepItem>
          <StepItem step={step} n={3} title="New password" sub={step < 3 ? "At least 8 characters" : null}>
            <PasswordInput id="new-pass" value={password} onChange={(e) => setPassword(e.target.value)} testid="forgot-new-password" autoComplete="new-password" />
          </StepItem>
        </ol>
        {error && <div className={`${box} mt-3`} data-testid="forgot-error">{error}</div>}
        <Button type="submit" disabled={busy} className={`${dark} mt-4`} data-testid="forgot-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : step === 1 ? "Send" : step === 2 ? "Continue" : "Save new password"}
        </Button>
      </form>
      {phoneOn && <p className="mt-4 text-xs text-stone-500 text-center">Signed up with your mobile number? You don't need a password: <Link to={`/login${nextQuery(next)}`} className="text-teal hover:underline">sign in with a code</Link>.</p>}
    </Shell>
  );
}
